<template>
    <div>
        <notification-modal v-model:is-open="showReportModal" v-model:is-error-modal="reportModalError">
            <template #title-content>
                {{ modalTitle }}
            </template>

            {{ modalMessage }}
        </notification-modal>
        <base-report>
            <h3 class="text-left text-lg font-semibold">Room Condition Report</h3>
            <template #left-column>
                <div class="mb-4 lg:mb-12">
                    <p>
                        The Room Condition report is designed to give a snapshot status update of your site’s room
                        conditions and compares the readings against their alarm profile requirements.
                    </p>
                    <p>
                        You can run the report right now by selecting the zone below that you wish to check on & the
                        time. Clicking run will email the report to you shortly with the current reading status of all
                        rooms on the selected zones & the comparison against their alarm threshold requirements.
                    </p>
                    <p>
                        To The right you can set up scheduled emails of the report to the selected users.  You can set
                        the time that the report will be generated and sent to the below list of users each day.
                    </p>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <label for="month">Day & Time to End the Interval</label>
                    <date-time-picker   id="month"
                                        v-model="month"
                                        :format="format"
                                        :view-mode="viewMode"
                                        :min-date="minDate"
                                        :max-date="maxDate"
                    ></date-time-picker>
                </div>

                <!-- Select Zones to report on -->
                <div    class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <label  for="zones[]">Select Zones to report on.</label>
                    <select2    id="zones[]"
                                :multiple="true"
                                :options="zones"
                                v-model:value="selectedZones"
                                :allow-clear="true"
                    ></select2>
                </div>

                <div class="flex justify-center">
                    <button v-if="isRunning"
                            class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center
                            px-4 py-2 border border-transparent text-sm bg-blue-600 text-white font-medium py-2 px-4
                            rounded opacity-50 cursor-not-allowed">
                        <span class="lnr icon-loading animate-spin mr-3"></span>
                        Running Report, Please Wait
                    </button>
                    <button v-else-if="selectedZones.length === 0"
                            class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center
                            px-4 py-2 border border-transparent text-sm bg-blue-600 text-white font-medium py-2 px-4
                            rounded opacity-50 cursor-not-allowed">
                        Please Select At Least One Zone
                    </button>
                    <button v-else
                            class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center px-4 py-2 border
                            border-transparent text-sm leading-5 font-medium rounded-md text-white bg-blue-600
                            hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:border-blue-700
                            active:bg-blue-700"
                            @click="runReport">
                        Run Report
                    </button>
                </div>
            </template>

            <template #right-column>
                <h4 class="text-center">Times To Send Report Each Day</h4>
                <!-- Time to send -->
                <div    v-for="(sendTime, index) in sendTimes"
                        class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <span>Time to Send {{ index > 0 ? index + 1 : '' }}</span>
                    <div>
                        <date-time-picker
                            v-model:model-value="sendTimes[index]"
                            format="h a"
                            vertical-position="bottom"
                            :id="`auto_send_time_${index}`"
                        ></date-time-picker>
                    </div>
                </div>

                <h4 class="text-center">Automatic Sending Settings</h4>

                <!-- Users to Send Automatically -->
                <div    v-if="permissions.can('manage_room_condition_report')"
                        class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
                    <span>Users to Send to Automatically</span>
                    <select2
                        :multiple="true"
                        :options="users"
                        v-model:value="selectedUsers"
                    ></select2>
                </div>

                <div class="flex justify-center">
                    <button v-if="isSaving"
                            class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white rounded opacity-50 cursor-not-allowed">
                        <span class="lnr icon-loading animate-spin mr-3"></span>
                        Saving Settings, Please Wait
                    </button>
                    <button v-else-if="permissions.can('manage_room_condition_report')"
                            class="inline-flex justify-center items-center w-1/2 ml-2 px-4 py-2 border-2 border-blue-200 text-md leading-5 font-sm
                            font-sans rounded-md text-blue-700 bg-white hover:bg-blue-600 hover:text-white
                            focus:outline-none focus:ring-blue-200 focus:border-blue-400 active:bg-blue-600"
                            @click="saveSettings">
                        Save Settings
                    </button>
                </div>
            </template>
        </base-report>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import BaseReport from "./BaseReport"
import Select2 from "@/components/elements/select2"
import Toggle from "@/components/elements/toggle"
import DateTimePicker from "@/components/Reports/DateTimePicker";

import axios from "axios";
import NotificationModal from "@/components/elements/NotificationModal";

export default {
    name: "RoomConditionReport",
    mixins: [SonicuCoreMixin],
    components: {NotificationModal, DateTimePicker, BaseReport, Select2, Toggle},
    props: {
        users: Object,
        selectedUsersInput: Array,
        meters: Object,
        zones: Object,
        autoSendHours: Array,
        minDate: Date,
        maxDate: Date,
        reportQueued: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            month: this.maxDate,
            format: 'MMMM DD, YYYY HH:mm',
            viewMode: 'days',
            isSaving: false,
            isRunning: false,

            selectedUsers: this.selectedUsersInput,
            selectedMeters: [],
            selectedZones: [],
            sendTimes: [],

            showReportModal: false,
            reportModalError: false,
            modalMessage: '',
            modalTitle: '',
            isErrorModal: false,
        }
    },
    created() {
        this.selectedUsers = this.selectedUsersInput


        this.autoSendHours.forEach(hour => {
            let date = new Date()
            date.setHours(hour)
            this.sendTimes.push(_.clone(date))
        })
    },
    mounted() {
        this.month.setMinutes(0)
    },
    methods: {
        saveSettings() {
            this.isSaving = true
            let users = []
            Object.keys(this.selectedUsers).forEach(key => {
                users.push(this.selectedUsers[key].toString())
            })
            let hours = []
            this.sendTimes.forEach(time => {
                hours.push(time.getHours() % 24);
            })

            let formData = {
                subscribed_users: users,
                auto_send_hours: hours,
            }
            axios.post(this.company.buildCompanyRoute('reports/room-condition-report/update'), formData)
                .then(response => {
                    this.modalMessage = 'Successfully saved report settings.'
                    this.modalTitle = 'Report Saved'
                    this.reportModalError = false
                    this.showReportModal = true
                })
                .catch(error => {
                    this.modalMessage = 'Error saving report settings.'
                    this.modalTitle = 'Error Saving'
                    this.reportModalError = true
                    this.showReportModal = true
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        runReport() {
            this.isRunning = true
            let formData = {
                month: this.month.toISOString(),
                selected_zones: this.selectedZones,
            }
            axios.post(this.company.buildCompanyRoute('reports/room-condition-report'), formData)
                .then(response => {
                    this.modalTitle = 'Report Queued'
                    this.modalMessage = 'You will receive an email when the report is prepared.'
                    this.reportModalError = false
                    this.showReportModal = true
                })
                .catch(error => {
                    this.modalTitle = 'Report Error'
                    this.modalMessage = 'Error running report, please try again later.'
                    this.reportModalError = true
                    this.showReportModal = true
                })
                .finally(() => {
                    this.isRunning = false
                })

        },
        zeropadTime(value, length = 2) {
            if (!(value instanceof String)) {
                value = value.toString()
            }
            while(value.length < length) {
                value = '0' + value
            }
            return value
        },
    },
    watch: {
        selectedZones: {
            handler(value, oldValue) {
                let allIndex = value.indexOf('all')
                if (allIndex !== -1) {
                    let newSelectedZones = Object.keys(this.zones)
                    let zonesAllIndex = newSelectedZones.indexOf("all")
                    newSelectedZones.splice(zonesAllIndex, 1)
                    this.selectedZones = newSelectedZones
                }
            }, deep: true
        },
        interval(val) {
            if (_.indexOf(['weekly'], val) > -1) {
                this.format = 'MMMM DD, YYYY'
                this.viewMode = 'days'
            }
            else if (_.indexOf(['monthly', 'semi-monthly'], val) > -1) {
                this.format = 'MMMM YYYY'
                this.viewMode = 'months'
            }
            this.semiMonthSection = this.month.getDate() <= 15 ? 'first' : 'last'
        },
        semiMonthSection(val) {
            if (val === 'first') {
                this.month.setDate(1)
            }
            else {
                this.month.setDate(16)
            }
        },
        month(val, old) {
            if (_.indexOf(['semi-monthly'], this.interval) > -1) {
                this.month.setDate(old.getDate())
            }

        }
    },
    computed: {
    },
}
</script>

<style scoped>

</style>
